import { useApp } from '@/context/AppContext'
import { useFilteredBreakValues } from '@/hooks/useFilteredBreakValues'
import { HorizontalStackedBar } from '@/components/charts/HorizontalStackedBar'
import { BarGrid } from '@/components/charts/BarGrid'
import { buildFilterKey } from '@/services/filterEngine'
import { getResponseColor } from '@/utils/colors'

// Response order: Strongly support → Support → Neutral/Unsure → Oppose → Strongly oppose
const SUPPORT_RESPONSE_ORDER = ['Strongly support', 'Support', 'Undecided/Neutral', 'Neutral / Unsure', 'Neutral/Unsure', 'Neutral', 'Unsure', 'Oppose', 'Strongly oppose']

// Legend labels (clean display)
const LEGEND_LABELS = ['Strongly support', 'Support', 'Neutral / Unsure', 'Oppose', 'Strongly oppose']

// Sort responses according to support order
const sortSupportResponses = (responses: Array<{ label: string; percentage: number }>) => {
  return [...responses].sort((a, b) => {
    const aIdx = SUPPORT_RESPONSE_ORDER.indexOf(a.label)
    const bIdx = SUPPORT_RESPONSE_ORDER.indexOf(b.label)
    if (aIdx === -1) return 1
    if (bIdx === -1) return -1
    return aIdx - bIdx
  })
}

// IPA Support questions with split sample
const IPA_SUPPORT_QUESTIONS = [
  {
    id: 'Support_IPA_Communities_SPLIT',
    label: 'Indigenous protected areas (IPAs) are protected areas that are designed and managed by Indigenous communities.',
    emphasis: 'communities'
  },
  {
    id: 'Support_IPA_Governments_SPLIT',
    label: 'Indigenous protected areas (IPAs) are protected areas that are designed and managed by Indigenous governments.',
    emphasis: 'governments'
  },
] as const

// Coded open-ended question for why they support/oppose IPAs
const WHY_SUPPORT_QUESTION = {
  id: 'OpenEnded_WhySaidThat',
  title: 'Why Do You Say That? (UNAIDED)',
  text: 'Open-ended responses coded by theme.',
}

// Fixed label order and colors for Why Support/Oppose IPAs
// Colors: Blue = positive/supportive, Red = negative/skeptical
// Order: Positive → Negative (like other open-ended pages)
const WHY_SUPPORT_CONFIG: Array<{ label: string; shortLabel: string; color: string }> = [
  // Pro-support reasons (blue gradient - positive)
  { label: 'Support for indigenous sovereignty and self determination', shortLabel: 'Sovereignty', color: '#1e3a5f' },
  { label: 'Historical rights and justice', shortLabel: 'Rights', color: '#2c5985' },
  { label: 'Compensation for past wrongs', shortLabel: 'Compensation', color: '#4e79a7' },
  { label: 'Environmental sustainability and protection', shortLabel: 'Environment', color: '#7eb0d5' },
  // Skeptical reasons (red gradient - negative)
  { label: 'Skepticism about Indigenous control', shortLabel: 'Skepticism', color: '#e15759' },
  { label: 'Concern about special treatment', shortLabel: 'Special treatment', color: '#a01a1c' },
]

// IPA Images - What comes to mind when you hear "Indigenous protected areas"
// Colors: Blue = positive/affirmative, Gray = neutral (middle), Red = negative/skeptical
// Order: Positive → Neutral → Negative
// Excludes N/A and Unsure responses
const IPA_IMAGES_CONFIG: Array<{ label: string; shortLabel: string; color: string }> = [
  // Positive/Affirmative (blue gradient - dark to light)
  { label: 'Land and environmental protection/Stewardship', shortLabel: 'Land protection', color: '#1e3a5f' },
  { label: 'Indigenous ownership/Control/Governance', shortLabel: 'Ownership', color: '#2c5985' },
  { label: 'Indigenous rights/Heritage/Culture', shortLabel: 'Rights', color: '#4e79a7' },
  { label: 'Positive attitudes & respect', shortLabel: 'Positive', color: '#7eb0d5' },
  { label: 'Collaboration & understanding', shortLabel: 'Collaboration', color: '#a8d4f0' },
  { label: 'Sacredness & spirituality', shortLabel: 'Sacred', color: '#b8d4e8' },
  // Neutral (gray - in the middle)
  { label: 'Reserves & treaty lands', shortLabel: 'Reserves', color: '#8e8e8e' },
  // Negative/Skeptical (red gradient - at the end)
  { label: 'Exclusion & limited access', shortLabel: 'Exclusion', color: '#e15759' },
  { label: 'Opposition/Corruption/Unfair', shortLabel: 'Opposition', color: '#a01a1c' },
]

// Indigenous Led Conservation - What comes to mind
// Colors: Blue = positive/affirmative, Red = negative/skeptical
// Excludes N/A and Unsure responses
const INDIGENOUS_CONSERVATION_CONFIG: Array<{ label: string; shortLabel: string; color: string }> = [
  // Positive/Affirmative (blue gradient)
  { label: 'Protection/Conservation of land and waters', shortLabel: 'Land/water', color: '#1e3a5f' },
  { label: 'Indigenous rights & justice', shortLabel: 'Rights', color: '#2c5985' },
  { label: 'Community, cultural heritage, and stewardship', shortLabel: 'Community', color: '#4e79a7' },
  { label: 'Collaboration and mutual understanding', shortLabel: 'Collaboration', color: '#7eb0d5' },
  { label: 'Positive association & support', shortLabel: 'Positive', color: '#a8d4f0' },
  // Negative (red - at the end)
  { label: 'Negative association & opposition', shortLabel: 'Negative', color: '#a01a1c' },
]

const WHAT_COMES_TO_MIND_QUESTIONS = [
  {
    id: 'OpenEnded_IPAImages',
    text: 'What words or images come to mind when you hear the term "Indigenous protected areas (IPA)"? (UNAIDED)',
    config: IPA_IMAGES_CONFIG,
  },
  {
    id: 'OpenEnded_IndigenousConservation',
    text: 'What words or images come to mind when you hear the term "Indigenous led conservation"? (UNAIDED)',
    config: INDIGENOUS_CONSERVATION_CONFIG,
  },
] as const

export function IndigenousProtectedAreas() {
  const { state } = useApp()
  const { data, filters } = state
  const { selectedBreak, selectedProvince } = filters

  const breakDef = data?.breaks.find(b => b.id === selectedBreak)
  const { breakValues: filteredBreakValues } = useFilteredBreakValues()
  const breakValues = selectedBreak !== 'total' && breakDef ? filteredBreakValues : ['total']
  const isBreakActive = selectedBreak !== 'total'
  const breakName = breakDef?.name || 'Total'
  const displayMap = breakDef?.displayMap || {}
  const getDisplayName = (value: string) => displayMap[value] || value

  // Get coded open-ended data for BarGrid
  const whyQuestion = data?.questions[WHY_SUPPORT_QUESTION.id]

  // Build columns from config (only include items that exist in data)
  const sampleData = whyQuestion?.data[buildFilterKey('total', 'total')]
  const dataLabels = new Set(sampleData?.responses?.map(r => r.label) || [])

  const whyColumns = WHY_SUPPORT_CONFIG
    .filter(c => dataLabels.has(c.label))
    .map(c => ({
      id: c.label,
      label: c.label,
      shortLabel: c.shortLabel,
      color: c.color,
    }))

  // Build rows for BarGrid
  const whyRows = whyQuestion ? breakValues.map(value => {
    const filterKey = buildFilterKey(
      'total',
      selectedBreak,
      isBreakActive ? value : undefined,
      selectedProvince
    )
    let questionData = whyQuestion.data[filterKey]

    if (!questionData && selectedProvince) {
      const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
      questionData = whyQuestion.data[fallbackKey]
    }

    if (!questionData) return null

    // Build values map
    const values: Record<string, number> = {}
    questionData.responses.forEach(r => {
      values[r.label] = r.percentage
    })

    return {
      id: value,
      label: isBreakActive ? getDisplayName(value) : 'National Sample',
      values,
    }
  }).filter((row): row is NonNullable<typeof row> => row !== null) : []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
          Indigenous Protected Areas
        </h1>
        <span className="text-sm text-slate-500 dark:text-slate-400">Break: {breakName}</span>
      </div>

      {/* IPA Support Section */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
        <p className="text-slate-600 dark:text-slate-300 mb-2">
          Do you <strong>SUPPORT</strong> or <strong>OPPOSE</strong> the establishment of more Indigenous protected areas (IPAs) in your province?
        </p>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-6 italic">
          Note: This question was asked with a split sample - half saw "communities" and half saw "governments"
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-slate-100 dark:border-slate-700">
          {LEGEND_LABELS.map(label => (
            <div key={label} className="flex items-center gap-2">
              <div
                className="w-4 h-4 rounded"
                style={{ backgroundColor: getResponseColor('support', label) }}
              />
              <span className="text-xs text-slate-600 dark:text-slate-400">{label}</span>
            </div>
          ))}
        </div>

        <div className="space-y-6">
          {!data ? (
            <p className="text-slate-500 dark:text-slate-400">Loading...</p>
          ) : (
            IPA_SUPPORT_QUESTIONS.map(({ id, label, emphasis }) => {
              const question = data.questions[id]
              if (!question) return null

              const rows = breakValues.map(value => {
                const filterKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined, selectedProvince)
                let questionData = question.data[filterKey]
                if (!questionData && selectedProvince) {
                  questionData = question.data[buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)]
                }
                return { value, filterKey, data: questionData }
              }).filter(row => row.data)

              if (rows.length === 0) return null

              return (
                <div key={id} className="border-b border-slate-100 dark:border-slate-700 pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    {label}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                    Split sample version emphasizing: <strong className="text-slate-600 dark:text-slate-300">{emphasis}</strong>
                  </p>
                  <div className="space-y-2">
                    {rows.map(row => (
                      <div key={row.filterKey} className="flex items-center gap-2">
                        <div className="w-32 text-xs text-slate-500 dark:text-slate-400 text-right shrink-0" title={isBreakActive ? getDisplayName(row.value) : 'National Sample'}>
                          {isBreakActive ? getDisplayName(row.value) : 'National Sample'}
                        </div>
                        <div className="flex-1">
                          <HorizontalStackedBar
                            segments={sortSupportResponses(row.data!.responses)}
                            scaleType="support"
                            height={isBreakActive ? 24 : 28}
                            showLabels={false}
                            normalize={true}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Why Support/Oppose - Coded Open-Ended using BarGrid */}
      {whyRows.length > 0 && whyColumns.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
          <BarGrid
            title={WHY_SUPPORT_QUESTION.title}
            description={WHY_SUPPORT_QUESTION.text}
            columns={whyColumns}
            rows={whyRows}
            colorIntensity={false}
          />
        </div>
      )}

      {/* What Comes to Mind - Open-ended coded responses */}
      {data && WHAT_COMES_TO_MIND_QUESTIONS.map(({ id, text, config }) => {
        const question = data.questions[id]
        if (!question) return null

        // Build columns from config (only include items that exist in data)
        const sampleData = question.data[buildFilterKey('total', 'total')]
        const dataLabels = new Set(sampleData?.responses?.map(r => r.label) || [])

        const columns = config
          .filter(c => dataLabels.has(c.label))
          .map(c => ({
            id: c.label,
            label: c.label,
            shortLabel: c.shortLabel,
            color: c.color,
          }))

        // Build rows for each break value
        const rows = breakValues.map(value => {
          const filterKey = buildFilterKey(
            'total',
            selectedBreak,
            isBreakActive ? value : undefined,
            selectedProvince
          )
          let questionData = question.data[filterKey]

          if (!questionData && selectedProvince) {
            const fallbackKey = buildFilterKey('total', selectedBreak, isBreakActive ? value : undefined)
            questionData = question.data[fallbackKey]
          }

          if (!questionData) return null

          // Build values map
          const values: Record<string, number> = {}
          questionData.responses.forEach(r => {
            values[r.label] = r.percentage
          })

          return {
            id: value,
            label: isBreakActive ? getDisplayName(value) : 'National Sample',
            values,
          }
        }).filter((row): row is NonNullable<typeof row> => row !== null)

        if (rows.length === 0 || columns.length === 0) return null

        return (
          <div key={id} className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
            <BarGrid
              title={text}
              columns={columns}
              rows={rows}
              colorIntensity={false}
            />
          </div>
        )
      })}
    </div>
  )
}
